import { Command } from "commander";
import chalk from "chalk";
import os from "os";
import path from "path";
import { getNodeVersion, getJavaVersion } from "../utils/exec.js";
import { findPackageJson, safeReadJson } from "../utils/files.js";
import { resolvePackageManager, getInstalledPackageManagers } from "../packageManager/packageManagerService.js";
import { detectProjectType } from "../project/projectDetector.js";
import { loadPluginsForProject } from "../plugins/loader.js";
import { analyzeDependencies } from "../analyze/dependencyAnalyzer.js";

async function getReactNativeVersion(): Promise<string | null> {
  const packageJsonPath = findPackageJson(process.cwd());
  if (!packageJsonPath) {
    return null;
  }
  const pkg = safeReadJson(packageJsonPath);
  if (!pkg) {
    return null;
  }
  const dependencies = (pkg["dependencies"] as Record<string, string>) || {};
  const devDependencies = (pkg["devDependencies"] as Record<string, string>) || {};
  const rnVersion = dependencies["react-native"] || devDependencies["react-native"];
  return rnVersion ? rnVersion.replace(/^[\^~>=<]+/, "") : null;
}

export const infoCommand = new Command("info")
  .description("Get information about the environment and React Native project")
  .option("--json", "Output information in JSON format")
  .action(async (options: { json?: boolean }) => {
    const cwd = process.cwd();
    const platform = os.platform();
    const arch = os.arch();
    const nodeVer = await getNodeVersion();
    const javaVer = await getJavaVersion();
    const rnVer = await getReactNativeVersion();
    const pmResolution = await resolvePackageManager(cwd);
    const installedPMs = await getInstalledPackageManagers();
    const installedNames = installedPMs.filter((pm) => pm.installed).map((pm) => pm.name);
    const projectType = detectProjectType(cwd);
    const loadedPlugins = await loadPluginsForProject(cwd);
    const pluginNames = loadedPlugins.map((p) => p.packageName);

    // Dependency & native module counts
    let depCount = 0;
    let nativeCount = 0;
    const packageJsonPath = findPackageJson(cwd);
    if (packageJsonPath) {
      const pkg = safeReadJson(packageJsonPath);
      if (pkg) {
        const projectRoot = path.dirname(packageJsonPath);
        const depAnalysis = analyzeDependencies(projectRoot, pkg as Record<string, unknown>);
        depCount = depAnalysis.totalDependencies;
        nativeCount = depAnalysis.nativeModules.length;
      }
    }

    if (options.json) {
      const data = {
        os: {
          platform,
          arch,
        },
        node: {
          version: nodeVer || "Not found",
        },
        java: {
          version: javaVer || "Not found",
        },
        reactNative: {
          version: rnVer || "Not found",
        },
        projectType,
        packageManager: {
          selected: pmResolution.selected,
          source: pmResolution.source,
          installed: installedNames,
        },
        plugins: pluginNames,
        dependencies: depCount,
        nativeModules: nativeCount,
      };
      console.log(JSON.stringify(data, null, 2));
      return;
    }

    console.log();
    console.log(chalk.bold("System Information"));
    console.log(`  OS:          ${chalk.cyan(platform)} (${arch})`);
    console.log(`  CPU:         ${os.cpus()[0]?.model || "Unknown"}`);
    console.log();
    console.log(chalk.bold("Binaries"));
    console.log(`  Node.js:     ${nodeVer ? chalk.cyan(nodeVer) : chalk.red("Not found")}`);
    console.log(`  Java:        ${javaVer ? chalk.cyan(javaVer) : chalk.red("Not found")}`);
    console.log();
    console.log(chalk.bold("Project Configuration"));
    console.log(`  React Native:    ${rnVer ? chalk.cyan(rnVer) : chalk.yellow("Not found in current folder")}`);
    console.log(`  Project Type:    ${chalk.cyan(projectType)}`);
    console.log(`  Dependencies:    ${chalk.cyan(depCount)}`);
    console.log(`  Native Modules:  ${chalk.cyan(nativeCount)}`);
    console.log(`  Package Manager: ${chalk.cyan(pmResolution.selected)} (source: ${pmResolution.source})`);
    console.log(`  Installed:       ${installedNames.join(", ")}`);
    console.log();
    console.log(chalk.bold("Plugins"));
    if (pluginNames.length > 0) {
      for (const name of pluginNames) {
        console.log(`  ${chalk.green("✓")} ${name}`);
      }
    } else {
      console.log("  No plugins loaded");
    }
    console.log();
  });
